import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';
import type {TextProps, ViewStyle} from 'react-native';
import {Pressable, StyleSheet, Text, View} from 'react-native';

import PhoneSvg from '../../../assets/phone.svg';
import ContentScrollView from '../../components/ContentScrollView';
import FixedWidthRowView from '../../components/FixedWidthRowView';
import type {_Array, _Object} from '../../Paramut';
import {useTheme, variance} from '../../styling';
import GroupListItemView from './GroupListItemView';

export type ListGroupsScreenProps = {
  phoneNumber?: string | null;
  phoneNumberDescription?: string | null;
  onPhoneNumberPress?: () => void;
  groups?: Groups | null;
  onGroupPress?: (id: number) => void;
  onGroupNotificationsPress?: (id: number) => void;
  localTime?: string;
  compensateHeaderHeight?: number;
};

export type Groups<Mut extends boolean = false> = _Array<Group<Mut>, Mut>;

export type Group<Mut extends boolean = false> = _Object<
  {
    id: number;
    name: string;
    address: string;
    upcomingEvent: {day: string; start: string} | null;
  },
  Mut
>;

export default observer(function ListGroupsScreen(
  props: ListGroupsScreenProps,
) {
  const {
    phoneNumber,
    phoneNumberDescription,
    onPhoneNumberPress,
    groups,
    onGroupPress,
    onGroupNotificationsPress,
    localTime,
    compensateHeaderHeight,
  } = props;

  const theme = useTheme();

  const contentContainerStyle: ViewStyle = useMemo(
    () => ({
      paddingTop: compensateHeaderHeight ?? 0,
    }),
    [compensateHeaderHeight],
  );
  const topIsCompensated = compensateHeaderHeight !== undefined;
  return (
    <ContentScrollView
      contentContainerStyle={contentContainerStyle}
      rootStyle={layoutStyles.root}
      topIsCompensated={topIsCompensated}>
      <FixedWidthRowView>
        <Pressable onPress={onPhoneNumberPress}>
          <View style={layoutStyles.phoneNumber}>
            <PhoneSvg
              style={layoutStyles.noShrink}
              color={theme.palette.textPrimary}
            />
            <LabelText style={layoutStyles.noShrink}>{phoneNumber}</LabelText>
            <LabelText style={layoutStyles.shrink}>
              {phoneNumberDescription}
            </LabelText>
          </View>
        </Pressable>
      </FixedWidthRowView>
      {!!localTime && (
        <FixedWidthRowView>
          <LabelText>Местное время: {localTime}</LabelText>
        </FixedWidthRowView>
      )}
      <FixedWidthRowView innerStyle={layoutStyles.gap}>
        {groups?.map(_ => (
          <GroupItem
            key={_.id}
            item={_}
            onGroupPress={onGroupPress}
            showNotificationButton={!!_.upcomingEvent}
            onGroupNotificationsPress={onGroupNotificationsPress}
          />
        ))}
      </FixedWidthRowView>
    </ContentScrollView>
  );
});

const layoutStyles = StyleSheet.create({
  root: {
    rowGap: 24,
    paddingBottom: 24,
  },
  phoneNumber: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  noShrink: {
    flexShrink: 0,
  },
  shrink: {
    flexShrink: 1,
  },
  gap: {
    gap: 32,
  },
});

const LabelText = variance(Text)(
  theme => ({
    root: {
      ...theme.text(),
      fontSize: 17,
      lineHeight: 21,
      color: theme.palette.textPrimary,
    },
  }),
  (): TextProps => ({
    numberOfLines: 1,
    ellipsizeMode: 'middle',
  }),
);

type GroupItemProps = {
  item: Group;
  onGroupPress?: (id: number) => void;
  showNotificationButton?: boolean;
  onGroupNotificationsPress?: (id: number) => void;
};

const GroupItem = observer(function GroupItem(props: GroupItemProps) {
  const {
    item,
    onGroupPress,
    showNotificationButton,
    onGroupNotificationsPress,
  } = props;

  const onPress = useCallback(() => {
    onGroupPress?.(item.id);
  }, [item, onGroupPress]);

  const onNotificationsPress = useCallback(() => {
    onGroupNotificationsPress?.(item.id);
  }, [item, onGroupNotificationsPress]);

  return (
    <Pressable onPress={onPress}>
      <GroupListItemView
        name={item.name}
        address={item.address}
        upcomingEvent={item.upcomingEvent}
        showNotificationButton={showNotificationButton}
        onNotificationsPress={onNotificationsPress}
      />
    </Pressable>
  );
});
